import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import uuid from "react-uuid";
import FlowContext from "../../../../contexts/flowContext";
import SidebarContext from "../../../../contexts/sidebar";
import UserContext from "../../../../contexts/user";
import SimulationContext from "../../../../contexts/simulation";
import InterAppCom from "../../../../utils/interAppCom";
import { flowToConfig } from "../../../Home/Controls/utils";
import ContextMenu from "../ContextMenu";
import DraggableZone from "./DraggableZone";
import styles from "./preview.module.css";

const WebAppPreview = () => {
  const [flowState, flowDispatcher] = useContext(FlowContext);
  const [userState] = useContext(UserContext);
  const [, dispatchSidebar] = useContext(SidebarContext);
  const [simulationState, simulationDispatcher] = useContext(SimulationContext);

  const [webAppCommunicator, setWebAppCommunicator] = useState(null);

  const [communicatorStatus, setCommunicatorStatus] = useState("inactive");
  const [communicatorSession, setCommunicatorSession] = useState(null);
  const [webAppDetail, setWebAppDetail] = useState({});
  const [mouseOverPreview, setMouseOverPreview] = useState(false);
  const [rotated, setRotated] = useState(false);

  const [active, setActive] = useState(true);
  const [collapsed, setCollapsed] = useState(true);
  const [initPosition, setInitPosition] = useState(false);
  const [interactiveIframe, setInteractiveIframe] = useState(true);
  const [previewIsLoaded, setPreviewIsLoaded] = useState(false);

  const [nodeToEdit, setNodeToEdit] = useState(null);

  const frameRef = useRef(null);
  const previewRef = useRef(null);

  useEffect(() => {
    if (simulationState.preview && simulationState.app === "web-app") {
      setCollapsed(false);
      setInitPosition(true);
    } else if (!simulationState.preview && simulationState.app === "web-app") {
      setCollapsed(true);
      setInitPosition(true);
    }
  }, [simulationState.app, simulationState.preview]);

  useEffect(() => {
    if (nodeToEdit) {
      const node = flowState.nodes.filter(
        (node) => node.data.name + "-" + node.id === nodeToEdit
      )[0];
      dispatchSidebar({
        type: "open",
        value: true,
        manager: "node",
      });
      flowDispatcher({
        type: "unselect-all-edges",
      });
      flowDispatcher({
        type: "set-selected-node",
        node,
      });
      flowState.rfInstance.fitBounds(
        { x: node.position.x, y: node.position.y, width: 100, height: 100 },
        { duration: 1000, zoomLevel: 1 }
      );
    }
  }, [nodeToEdit]);

  useEffect(() => {
    // make nodeToEdit to null if not any selected node
    if (!flowState.nodes.filter((node) => node.selected).length) {
      setNodeToEdit(null);
    }
  }, [flowState.nodes]);

  const handleMessage = function (data) {
    switch (data.action) {
      case "init":
        console.log("init 🤝 webapp to maci");
        setCommunicatorStatus("active");
        setCommunicatorSession(uuid());
        setWebAppDetail({
          ...data.detail,
          config: webAppDetail.config,
        });
        break;
      case "update-flow":
        console.log("update flow coming from webapp", data.flow);
        setWebAppDetail({
          ...webAppDetail,
          flow: data.flow,
          answers: data.answers,
        });
        break;
      case "edit-node":
        setNodeToEdit(data.detail.nodeId);
        console.log("edit node", data.detail.nodeId);
        break;
      case "terminate":
        setCommunicatorStatus("inactive");
        break;

      default:
        console.log("unknown action", data);
        break;
    }
  };

  const getFlow = useCallback(() => {
    const selectedNode = flowState.nodes.filter((node) => node.selected)[0];

    return {
      edges: flowState.edges,
      nodes: flowState.nodes.map((node) => {
        if (selectedNode) {
          return {
            ...node,
            data: {
              ...node.data,
              starter: selectedNode.id === node.id,
            },
          };
        } else {
          return node;
        }
      }),
      globalParameters: flowState.globalParameters,
    };
  }, [flowState.globalParameters, flowState.nodes, flowState.edges]);

  useEffect(() => {
    if (webAppCommunicator && communicatorSession) {
      const flow = getFlow();
      const config = flowToConfig(flow, userState.currentApp);

      webAppCommunicator.sendMessage({
        action: "update",
        detail: {
          ...webAppDetail,
          config,
        },
      });
    }
  }, [getFlow, webAppCommunicator, communicatorStatus, communicatorSession]);

  useEffect(() => {
    if (!active) return;
    // initialize communicator
    const communicator = new InterAppCom(
      process.env.REACT_APP_WEB_APP_URL,
      process.env.REACT_APP_WEB_APP_URL,
      document.getElementById("frame").contentWindow,
      handleMessage
    );
    setWebAppCommunicator(communicator);

    return () => {
      communicator.cleanUp();
    };
  }, [active]);

  //region Reduce position with animation
  useEffect(() => {
    if (webAppCommunicator && previewIsLoaded) {
      webAppCommunicator.sendMessage({
        action: "handshake",
      });
    }
  }, [webAppCommunicator, previewIsLoaded]);

  useEffect(() => {
    previewRef.current.style.top = "100px";
    previewRef.current.style.left = window.innerWidth - 340 + "px";
    setTimeout(() => {
      setInitPosition(false);
    }, 250);
  }, [initPosition]);
  //endregion

  const handleQrCodeDemoGeneration = function () {
    /*
     * appel api back:
     * publier avec un identifiant: hash(nom_du_flow + user_name)
     * au retour de la réponse, générer un qrcode ave une url 'base_url/hash'
     *  */
  };

  const reducePreviewWindow = function () {
    setCollapsed(!collapsed);
    simulationDispatcher({
      type: "toggle-webapp-preview",
    });
  };

  return (
    <div
      className={`${active ? styles.active_preview : styles.inactive_preview}
                 ${styles.preview_container}
                  ${collapsed ? styles.collapsed_preview : ""}
                 ${
                   initPosition || collapsed
                     ? styles.animate_preview_container
                     : ""
                 }
                  ${rotated ? styles.preview_container_rotated : ""}
                  web-app-live-preview`}
      onMouseEnter={() => setMouseOverPreview(true)}
      onMouseLeave={() => setMouseOverPreview(false)}
      ref={previewRef}
    >
      {active ? (
        <iframe
          src={`${process.env.REACT_APP_WEB_APP_URL}/live-preview`}
          title="web-app-simulation"
          frameBorder="0"
          width="100%"
          height="100%"
          id="frame"
          ref={frameRef}
          allow="microphone"
          style={{
            pointerEvents: interactiveIframe ? "auto" : "none",
          }}
          onLoad={() => {
            setPreviewIsLoaded(true);
          }}
        />
      ) : (
        <button onClick={() => setActive(true)}>activate</button>
      )}
      <img
        src={window.location.origin + "/iphone.png"}
        alt=""
        className={`${styles.iphone_frame} ${
          collapsed ? styles.collapsed_preview : ""
        } ${rotated ? styles.iphone_frame_rotated : ""}`}
      />

      <DraggableZone
        draggable={previewRef}
        onStartDragging={() => setInteractiveIframe(false)}
        onStopDragging={() => setInteractiveIframe(true)}
      />

      {mouseOverPreview && (
        <ContextMenu
          options={[
            {
              value: "Refresh",
              handler: function () {
                setPreviewIsLoaded(false);
                frameRef.current.src = `${process.env.REACT_APP_WEB_APP_URL}/live-preview`;
              },
            },
            {
              value: "Reduce",
              handler: reducePreviewWindow,
            },
            {
              value: "Screenshot",
              handler: function () {},
            },
            {
              value: "Rotate",
              handler: function () {
                setRotated(!rotated);
              },
            },
            {
              value: "QR Code demo",
              handler: handleQrCodeDemoGeneration,
            },
          ]}
          reference={previewRef}
        />
      )}
    </div>
  );
};

export default WebAppPreview;
